import * as QueryKeys from "data";

import { useIdentity } from "contexts/identity-context";

import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { fetchDatasets, fetchDefinitions } from "data/queries-common";
import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { resolveIdentityId } from "utils/identity";
import { customColumns, customersToRows } from "./columns-rows";

import {
  DatasetDefinitions,
  DatasetFiltersType,
  DatasetList,
} from "data/models";
import { useLocation } from "react-router-dom";

import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomServerPaginatedTable from "components/shared/server-paginated-table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as localStorageKeys from "data/local-storage-keys";
import usePaginatedTableQuery from "shared-hooks/use-paginated-table-query";
import FilterComponent from "../share-component/filter-component";
import { updateQueryAndFilters } from "../utils";
import { DatasetRow } from "./columns-rows";
import { DatasetFilters } from "./filters";

export const DownloadPage = () => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const urlLocationQuery = useLocation().search;

  const savedSearch = localStorage.getItem(localStorageKeys.searchDatasets);
  const { enqueueLinkCorruptedSnackbar } = useCustomSnackbars();

  let initialQueryFilterState: DatasetFiltersType = {
    query: "",
    product: [],
    region: [],
    format: [],
    datum: [],
    release: "",
    latestRelease: false,
    licenced: false,
    includeChildProducts: true,
  };

  const [queryFilterState, setQueryFilterState] = useState<DatasetFiltersType>(
    initialQueryFilterState,
  );
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);

  const { paginationModel, handleChangePaginationModel, searchQuery } =
    usePaginatedTableQuery<DatasetList>(
      (limit: number, offset: number) => {
        return fetchDatasets(queryFilterState, limit, offset);
      },
      QueryKeys.datasetsSearchQuery,
      identityId,
      queryFilterState,
    );

  const datasetDefinitions: UseQueryResult<DatasetDefinitions, unknown> =
    useQuery({
      queryKey: [QueryKeys.definitionsKey],
      queryFn: () => fetchDefinitions(),
    });

  useEffect(() => {
    if (!datasetDefinitions.isSuccess) {
      return;
    }
    updateQueryAndFilters({
      savedSearch,
      setQueryFilterState,
      setLoadingFilters,
      urlLocationQuery,
      initialQueryFilterState,
      definitions: datasetDefinitions,
      enqueueLinkCorruptedSnackbar,
    });
  }, [datasetDefinitions.isSuccess]);

  const getCustomRowLicence = (row: DatasetRow): string => {
    if (row.licenced == false) {
      return "text-muted-foreground";
    }
    return "";
  };

  const customColumns_ = customColumns();

  const isLoading = searchQuery.isPending;

  const rows = searchQuery.data ? customersToRows(searchQuery.data) : [];

  return (
    <>
      <div className="flex flex-col w-full gap-4 relative">
        <ReactQueryErrorWrapper
          queries={[searchQuery, datasetDefinitions]}
          mutations={[]}
        />
        <Alert>
          <Info className="size-4" />
          <AlertTitle>
            <b>Changes for National Buildings 4.0 and Insight Packs</b>
          </AlertTitle>
          <AlertDescription>
            In preparation for the <b>March National Buildings V4.0 launch</b>,
            we have introduced <b>Insight Packs</b>. This update adds a
            dedicated <b>Insight Pack toggle</b> and includes, Height, Roof and
            Solar Insight Packs + Pool Insight Pack (located under ‘Address –
            Pool’). All Insight Packs can be easily managed using the standard
            product filter and the toggle.
          </AlertDescription>
        </Alert>

        <FilterComponent
          queryFilterState={queryFilterState}
          setQueryFilterState={setQueryFilterState}
          initialQueryFilterState={initialQueryFilterState}
          renderFilter={DatasetFilters}
          definitions={datasetDefinitions}
          localStorageKey={localStorageKeys.searchDatasets}
        />

        <CustomServerPaginatedTable
          rowCount={searchQuery.data?.links?.numberMatched ?? -1}
          rows={rows}
          getCustomRowClass={getCustomRowLicence}
          paginationModel={paginationModel}
          setPaginationModel={handleChangePaginationModel}
          cols={customColumns_}
          isLoading={isLoading}
          overlayText={
            "We couldn't find any datasets that match your filter criteria."
          }
          disableColumnMenu
          disableColumnSelector
        />
      </div>
    </>
  );
};
